<template>
  <div class="px-2">
    <b-card class="w-100 d-flex rounded-lg">
      <b-row class="mb-2 align-items-center">
        <b-col md="4">
          <b-form-group
            label="Experiment ID"
            label-for="experiment-id"
          >
            <v-select
              id="experiment-id"
              v-model="filters.experiment_id"
              :options="experimentsLookups"
              label="name"
              :reduce="(experiment) => experiment.id"
              placeholder="Filter by Experiment"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @input="refresh"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Assignment"
            label-for="assignment-id"
          >
            <v-select
              v-model="filters.activity_code"
              :options="localAssignmentsLookups"
              label="name"
              :reduce="(assignment) => assignment.assignment_id"
              placeholder="Filter by Assignment"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @input="refresh"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="4"
          class="d-flex align-items-end justify-content-end"
        >
          <b-button
            class="btn rounded-pill"
            variant="primary"
            @click="createActivity"
          >
            Create Activity
          </b-button>
        </b-col>
      </b-row>

      <center v-if="isLoading">
        <b-spinner
          variant="primary"
          label="Spinning"
        />
      </center>
      <b-row class="py-2">
        <center
          v-if="!isLoading && activities.data && activities.data.length === 0"
          class="w-100 text-center mr-3"
        >
          <h3>No Data to show</h3>
        </center>
        <b-table
          v-if="!isLoading && activities.data && activities.data.length > 0"
          striped
          hover
          :items="activities.data"
          :fields="fields"
        >
          <template #cell(experiment.name)="data">
            <b-link
              v-if="data.item.experiment && data.item.experiment.id"
              :to="{ name: 'experiment_details', params: { id: data.item.experiment.id } }"
              class="text-primary font-weight-bold"
            >
              {{ data.item.experiment.name }}
            </b-link>
            <span v-else>{{ (data.item.experiment && data.item.experiment.name) || "N/A" }}</span>
          </template>

          <template #cell(actions)="data">
            <b-link
              :to="{ name: 'view-activity', params: { id: data.item.id } }"
            >
              <feather-icon
                icon="EyeIcon"
                class="mr-50"
              />
            </b-link>
            <b-link
              :to="{ name: 'edit-activity', params: { id: data.item.id } }"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50" />
            </b-link>
            <b-link
              v-if="hasPermission('Delete Activity')"
              :tooltip="'none'"
              class="alert-link cutom-alert-link text-danger"
              @click="removeActivity(data.item.id)"
            >
              <feather-icon
                v-b-tooltip.hover.top="'Delete'"
                :tooltip="'none'"
                icon="Trash2Icon"
                size="18"
              />
            </b-link>
            <feather-icon
              v-if="!syncingIds.includes(data.item.id)"
              v-b-tooltip.hover.top="'Sync Activity'"
              icon="RefreshCwIcon"
              size="20"
              class="text-info cursor-pointer ml-1"
              @click="syncAllActivity(data.item.id)"
            />
            <feather-icon
              v-else
              v-b-tooltip.hover.top="'Syncing...'"
              icon="LoaderIcon"
              size="20"
              class="text-info cursor-pointer ml-1"
            />

          </template>
        </b-table>
      </b-row>

      <!-- Pagination & Per Page -->
      <b-row class="mt-2 w-100 align-items-center" v-if="activities && activities.meta">
        <b-col md="4" class="text-light d-flex align-items-center" style="font-size: 0.95rem;">
          <span v-if="activities.meta.total > 0" class="d-flex align-items-center" style="margin-left: 2px;">
            Showing 
            <b-form-select
              v-model="filters.per_page"
              :options="[10, 25, 50, 100]"
              class="custom-per-page mx-1"
              @change="onPageChange(1)"
            />
          </span>
        </b-col>
        <b-col md="4" class="d-flex justify-content-center">
          <b-pagination
            v-model="activities.meta.current_page"
            :total-rows="activities.meta.total || 0"
            :per-page="activities.meta.per_page || 0"
            @change="onPageChange"
            class="mb-0"
          />
        </b-col>
        <b-col md="4"></b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BSpinner,
  BContainer,
  BRow,
  BCol,
  BButton,
  BCard,
  BTable,
  VBTooltip,
  BLink,
  BFormGroup,
  BPagination,
  BFormInput,
  BDropdown,
  BDropdownItem,
  BFormSelect,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'

import vSelect from 'vue-select'

export default {
  name: 'LmsActivities',
  components: {
    vSelect,
    BLink,
    VBTooltip,
    BSpinner,
    BContainer,
    BTable,
    BRow,
    BCol,
    BButton,
    BCard,
    BPagination,
    BFormGroup,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BFormSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  computed: {
    ...mapGetters({
      activities: 'activities/items',
      experimentsLookups: 'experiments/experimentsLookups',
      localAssignmentsLookups: 'activities/LocalAssignmentsLookups',
      loadSyncAllActivity: 'activities/loadSyncAllActivity',
      loadLocalAssignmentsLookups: 'activities/loadLocalAssignmentsLookups',
    }),
    id() {
      return this.$route.params.id ? this.$route.params.id : null
    },
  },
  data() {
    return {
      fields: [
        // { key: "id", label: "ID", sortable: true },
        // { key: "activity_code", label: "Activity Code", sortable: true },
        { key: 'course_name', label: 'Course', sortable: true },
        { key: 'assignment_name', label: 'Assignment', sortable: true },
        { key: 'experiment.name', label: 'Experiment', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      isLoading: false,
      filters: {
        experiment_id: '',
        activity_code: '',
        institution_id: null,
        per_page: 10,
      },
      syncingIds: [],
    }
  },
  mounted() {
    this.init()
  },
  methods: {

    syncAllActivity(activity_id) {
      this.syncingIds.push(activity_id)
      this.$store.dispatch('activities/syncAllActivity', { id: activity_id }).then(response => {
        this.syncingIds = this.syncingIds.filter(id => id !== activity_id)
        this.$swal({
          icon: 'success',
          title: response.message || 'Activity synced successfully',
          showConfirmButton: true,
          confirmButtonColor: '#7367f0',
        })
      }).catch(error => {
        console.error(error)
        this.syncingIds = this.syncingIds.filter(id => id !== activity_id)
        this.$swal({
          icon: 'error',
          title: 'Failed to sync activity',
          showConfirmButton: true,
          confirmButtonColor: '#7367f0',
        })
      })
    },
    createActivity() {
      this.$router.push({ name: 'create-activity', params: { id: this.id } })
    },
    init() {
      this.isLoading = true
      this.$store.dispatch('activities/LocalAssignmentsLookups', this.id)
      this.$store.dispatch('experiments/experimentsLookups')
      this.filters.institution_id = this.id
      this.$store
        .dispatch('activities/list', { ...this.filters })
        .then(response => {
          this.isLoading = false
        })
        .catch(e => {
          this.isLoading = false
          console.log(e)
          this.$swal({
            icon: 'error',
            title: `<h4>Error fetching activities: ${e.message}</h4>`,
            showConfirmButton: true,
            confirmButtonColor: '#7367f0',
            allowOutsideClick: true,
          })
        })
    },
    refresh() {
      this.$store.dispatch('activities/list', {
        page: 1,
        ...this.filters,
      })
    },
    onPageChange(page) {
      this.$store.dispatch('activities/list', { page, ...this.filters })
    },
    removeActivity(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store
            .dispatch('activities/remove', id)
            .then(() => {
              this.refresh()
              this.$swal({
                icon: 'success',
                title: 'Deleted!',
                text: 'Your file has been deleted.',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
            })
            .catch(e => {
              console.log(e)
              this.$swal({
                icon: 'error',
                title: 'Error',
                text: `Error deleting activity: ${e.message}`,
                customClass: {
                  confirmButton: 'btn btn-primary',
                },
              })
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";

[dir] .dark-layout input.form-control,
[dir] .dark-layout .custom-file-label,
[dir] .dark-layout textarea.form-control {
  height: 45px;
  box-shadow: black beige indigo;
  border-radius: 8px;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.4);
  background: linear-gradient(
    90deg,
    rgba(158, 63, 217, 0.2) 0%,
    rgba(37, 78, 158, 0.2) 49%,
    rgba(70, 124, 227, 0.2) 100%
  );
  border: 2px solid #ffffff3c;
  border-left: none;
  border-right: none;
  border-top: none;
}
[dir] .dark-layout input.form-control:not(:focus),
[dir] .dark-layout .custom-file-label:not(:focus),
[dir] .dark-layout textarea.form-control:not(:focus) {
  border-color: #ffffff3c;
}

.field-icon {
  position: absolute;
  top: 10px;
  left: 30px;
  color: #ffffffcc !important;
  font-weight: 500;
}
</style>
